import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import s from './Overlay.module.css';
import { Icon } from './Icon';
import { Button } from './Button';

function useEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open, onClose]);
}

function useBodyLock(open: boolean) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);
}

export function Modal({ open, onClose, title, description, children, footer, size = 'md' }: {
  open: boolean; onClose: () => void; title: ReactNode; description?: ReactNode; children?: ReactNode; footer?: ReactNode; size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  useEscape(open, onClose);
  useBodyLock(open);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) {
      const el = ref.current?.querySelector<HTMLElement>('input, select, textarea, button:not([data-close])');
      el?.focus();
    }
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div className={s.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} className={[s.modal, s[size]].join(' ')} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
        <div className={s.head}>
          <div className={s.title}>{title}{description ? <div className={s.desc}>{description}</div> : null}</div>
          <button className={s.close} onClick={onClose} aria-label="Close" data-close><Icon name="x" /></button>
        </div>
        <div className={s.body}>{children}</div>
        {footer ? <div className={s.foot}>{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export function Drawer({ open, onClose, children, side = 'right', label }: { open: boolean; onClose: () => void; children: ReactNode; side?: 'left' | 'right'; label: string }) {
  useEscape(open, onClose);
  useBodyLock(open);
  if (!open) return null;
  return createPortal(
    <>
      <div className={s.drawerBackdrop} onClick={onClose} />
      <aside className={[s.drawer, s[side]].join(' ')} role="dialog" aria-modal="true" aria-label={label}>{children}</aside>
    </>,
    document.body,
  );
}

// ── Confirm ────────────────────────────────────────────────────────────────
interface ConfirmOpts { title: string; body?: ReactNode; confirmLabel?: string; danger?: boolean }
const ConfirmCtx = createContext<((o: ConfirmOpts) => Promise<boolean>) | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOpts & { resolve: (v: boolean) => void }) | null>(null);
  const confirm = useCallback((o: ConfirmOpts) => new Promise<boolean>((resolve) => setState({ ...o, resolve })), []);
  const close = (v: boolean) => { state?.resolve(v); setState(null); };
  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <Modal open={!!state} onClose={() => close(false)} title={state?.title ?? ''} size="sm"
        footer={<>
          <Button onClick={() => close(false)}>Cancel</Button>
          <Button variant={state?.danger ? 'danger' : 'primary'} onClick={() => close(true)}>{state?.confirmLabel ?? 'Confirm'}</Button>
        </>}>
        {state?.body ? <div style={{ color: 'var(--txt-body)' }}>{state.body}</div> : null}
      </Modal>
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const c = useContext(ConfirmCtx);
  if (!c) throw new Error('useConfirm outside ConfirmProvider');
  return c;
}

// ── Menu ───────────────────────────────────────────────────────────────────
export interface MenuItemDef {
  label?: ReactNode; icon?: string; onSelect?: () => void; to?: string; href?: string; danger?: boolean; disabled?: boolean;
  separator?: boolean; heading?: string;
}

export function Menu({ trigger, items, align = 'right', direction = 'down', label }: {
  trigger: (props: { onClick: () => void; 'aria-expanded': boolean; 'aria-haspopup': 'menu' }) => ReactNode;
  items: MenuItemDef[]; align?: 'left' | 'right'; direction?: 'up' | 'down'; label?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [open]);
  return (
    <div className={s.menuWrap} ref={ref}>
      {trigger({ onClick: () => setOpen((o) => !o), 'aria-expanded': open, 'aria-haspopup': 'menu' })}
      {open ? (
        <div className={[s.menu, align === 'right' ? s.alignRight : s.alignLeft, s[direction]].join(' ')} role="menu" aria-label={label}>
          {items.map((it, i) => {
            if (it.separator) return <div key={i} className={s.menuSep} role="separator" />;
            if (it.heading) return <div key={i} className={s.menuLabel}>{it.heading}</div>;
            const content = <>{it.icon ? <Icon name={it.icon} size={16} /> : null}<span className="grow">{it.label}</span></>;
            const cls = [s.menuItem, it.danger && s.danger].filter(Boolean).join(' ');
            if (it.to) return <Link key={i} to={it.to} className={cls} role="menuitem" onClick={() => setOpen(false)}>{content}</Link>;
            if (it.href) return <a key={i} href={it.href} className={cls} role="menuitem" target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>{content}</a>;
            return (
              <button key={i} type="button" className={cls} role="menuitem" disabled={it.disabled}
                onClick={() => { setOpen(false); it.onSelect?.(); }}>{content}</button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
